import React, { useCallback, useEffect, useState } from 'react'
import { isNil } from 'ramda'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { FormControlLabel, Typography } from '@material-ui/core'

import { usePlace } from 'commons/usePlace'
import { mapNumToBool } from 'commons/boolean'
import { useAppName } from 'commons/usePartner'
import { SECONDARY } from 'app/constants/input'

import Switchs from 'app/componentV4/Switchs'
import CheckBox from 'app/componentV4/CheckBox'
import SubmitDialog from 'app/componentV4/Dialog/SubmitDialog'
import PrimaryTooltip from 'app/componentV4/Tooltip/PrimaryTooltip'

import { useAction } from './useAction'
function AutoApprove({ onFetch = () => {} }) {
  const place = usePlace()
  const appName = useAppName()

  const { formatMessage } = useIntl()
  const {
    state: { countPending, isDialogApprovePending, isLoadingApprove },
    switchAutoApprove,
    fetchSubscribersPending,
    handleSubscribersPendingDialog,
  } = useAction(formatMessage)

  const [loading, setLoading] = useState(false)
  const [isChecked, setChecked] = useState(mapNumToBool(place.autoApprove))
  const [isAcceptAllPending, setIsAcceptAllPending] = useState(true)

  const submitAutoApprove = useCallback(
    async (bool, acceptAllPending = false) => {
      try {
        await switchAutoApprove(bool, acceptAllPending)
        onFetch()
      } catch (error) {
        setChecked(!bool)
      } finally {
        setLoading(false)
        handleSubscribersPendingDialog(false)
      }
    },
    [onFetch, switchAutoApprove, handleSubscribersPendingDialog],
  )

  const handleSubscribersPending = useCallback(
    async (bool) => {
      const count = await fetchSubscribersPending()
      if (!isNil(count) && count === 0) submitAutoApprove(bool)
      else return
    },
    [submitAutoApprove, fetchSubscribersPending],
  )

  const handleSwitch = useCallback(
    async (bool) => {
      setChecked(bool)
      setLoading(true)

      if (bool) {
        handleSubscribersPending(bool)
      } else submitAutoApprove(bool)
    },
    [submitAutoApprove, handleSubscribersPending],
  )

  const handleClose = () => {
    setChecked(mapNumToBool(place.autoApprove))
    setLoading(false)
    handleSubscribersPendingDialog(false)
  }

  useEffect(() => {
    if (isDialogApprovePending) setIsAcceptAllPending(true)
  }, [isDialogApprovePending])

  return (
    <div className="flex">
      <Switchs
        checked={isChecked}
        onChange={async (value) => {
          await handleSwitch(value.target.checked)
        }}
        disabled={loading}
      />
      <div className="flex items-center">
        <Typography className="text-14 text-primary font-500 mx-3 whitespace-nowrap">
          <FormattedMessage id="filter.autoapprove" />
        </Typography>
        <PrimaryTooltip
          title={formatMessage({
            id: 'subscriber.dialog.autoApprove.title',
          })}
        >
          <InfoOutlinedIcon
            style={{ color: '#183560', cursor: 'pointer' }}
            fontSize="small"
          />
        </PrimaryTooltip>
      </div>
      <SubmitDialog
        id="dialog-export-csv-team-member"
        open={isDialogApprovePending}
        type={SECONDARY}
        title={formatMessage({
          id: 'subscriber.dialog.autoApprove.title',
        })}
        description={formatMessage(
          {
            id: 'subscriber.dialog.autoApprove.body',
          },
          { appName: appName },
        )}
        submitText={formatMessage({
          id: 'filter.autoapprove',
        })}
        onSubmit={async () => {
          await submitAutoApprove(true, isAcceptAllPending)
        }}
        closeText={formatMessage({
          id: 'action.cancel',
        })}
        onClose={() => handleClose()}
        disabledSubmit={isLoadingApprove}
        isLoading={isLoadingApprove}
      >
        <div className="whitespace-nowrap text-primary text-14 font-normal w-fit m-auto">
          <FormControlLabel
            control={
              <CheckBox
                checked={isAcceptAllPending}
                onChange={(e) => {
                  setIsAcceptAllPending(e.target.checked)
                }}
                name={`pending-approve-checkbox`}
              />
            }
            label={formatMessage(
              {
                id: 'subscriber.dialog.autoApprove.checkboxMsg',
              },
              { pending: countPending },
            )}
          />
        </div>
      </SubmitDialog>
    </div>
  )
}

AutoApprove.propTypes = {
  onFetch: PropTypes.func,
}

export default AutoApprove
